import { DragEndEvent } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Tooltip } from 'antd';
import { MoveIcon } from 'assets/svg';
import React, { useContext, useMemo } from 'react';

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle = ({ title, disabled }: any) => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      disabled={disabled}
      className={`border-none bg-transparent shadow-none m-0 p-0 flex justify-center items-center`}
      ref={setActivatorNodeRef}
      {...(disabled ? {} : listeners)}
    >
      <Tooltip title={title} key={Math.random() * 1000}>
        <MoveIcon />
      </Tooltip>
    </Button>
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};
interface DragIndex {
  activeIndex?: number;
  overIndex?: number;
}

const onDragEnd = (
  { active, over }: DragEndEvent,
  setData: (data: any) => void,
  rowKey: any = 'id',
): DragIndex => {
  if (active.id !== over?.id) {
    let activeIndex;
    let overIndex;
    setData((prevState: any) => {
      activeIndex = prevState.findIndex((record: any) => {
        return record?.[rowKey] === active?.id;
      });
      overIndex = prevState.findIndex(
        (record: any) => record?.[rowKey] === over?.id,
      );
      return arrayMove(prevState, activeIndex, overIndex);
    });
    return { activeIndex, overIndex };
  }
  return { activeIndex: -1, overIndex: -1 };
};

export const Rows = { DragHandle, Row, onDragEnd };
